import {
  Box,
  Button,
  Field as ChakraField,
  Collapsible,
  HStack,
  NativeSelect,
  Switch,
  Text,
  useDisclosure,
  VStack
} from "@chakra-ui/react"
import { AugmentationPanelRow } from "@pathwright/ux/src/components/augmentation/AugmentationPanelRow"
import {
  AUGMENTATION_PANEL_SELECT_PROPS,
  AUGMENTATION_PANEL_STATIC_TEXT_PROPS,
  AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Field, Form, Formik, useFormikContext } from "formik"
import moment from "moment"
import { ChangeEvent } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  NotificationDigestInterval,
  useUpdateNotificationDigestIntervalMutation,
  useUpdateUserMembershipMutation
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

const schema = z.object({
  consentsToCurrent: z.boolean(),
  consentsToPrev: z.boolean().optional(),
  notificationEmailSummary: z.nativeEnum(NotificationDigestInterval)
})

const validate = toFormikValidate(schema)

type Values = z.infer<typeof schema>

// Handles the logic for updating the user's consent to notifications.
function useUpdateConsentToNotifications() {
  const { me, school } = usePathwrightContext()
  const { consent_snapshot, consent_dtime } = me?.membership || {}

  const schoolConsentDescription = school.consent_description || ""
  const consents = !!consent_snapshot
  const consentsToCurrent =
    consents && schoolConsentDescription === consent_snapshot
  const consentsToPrev = consents && !consentsToCurrent

  const [mutation] = useUpdateUserMembershipMutation({})

  function updateConsentToNotifications(consents: boolean) {
    const consent_snapshot = consents ? schoolConsentDescription : null

    const consent_dtime = consents ? new Date().toISOString() : null

    return mutation({
      variables: {
        consents
      },
      optimisticResponse: {
        updateMemberConsent: {
          id: me.id,
          consent_snapshot,
          consent_dtime,
          __typename: "Membership"
        }
      }
    })
  }

  return {
    updateConsentToNotifications,
    consentsToCurrent,
    consentsToPrev,
    schoolConsentDescription,
    consentSnapshot: consent_snapshot || "",
    consentDtime: consent_dtime
  }
}

function NotificationConsentSwitchField({
  field,
  label,
  meta
}: {
  field: "consentsToCurrent" | "consentsToPrev"
  label: string
  meta?: string
}) {
  const form = useFormikContext<Values>()

  return (
    <VStack w="100%" align="stretch" gap={0}>
      <AugmentationPanelRow
        asChild
        minH="augmentationPanelRowTouch"
        py="augmentationPanelGutter"
        css={{
          "& .chakra-switch__control": AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS
        }}
      >
        <Switch.Root
          id={field}
          name={field}
          checked={!!form.values[field]}
          onCheckedChange={async (details) => {
            await form.setFieldValue(field, details.checked)
            await form.submitForm()
          }}
          w="full"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="augmentationPanel"
          cursor="pointer"
        >
          <Switch.HiddenInput />
          <Switch.Label flex="1" minW={0} m={0}>
            <Text as="span" textStyle="augmentationPanel" m={0}>
              {label}
            </Text>
          </Switch.Label>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Root>
      </AugmentationPanelRow>
      {!!meta && (
        <Text
          fontSize="12px"
          px="augmentationPanelGutter"
          pb={1}
          {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}
        >
          {meta}
        </Text>
      )}
    </VStack>
  )
}

// Form for updating user's notification preferences.
function UserAugmentationNotificationsSettings() {
  const { t } = useTranslate()
  const { me } = usePathwrightContext()

  const {
    updateConsentToNotifications,
    consentsToCurrent,
    consentsToPrev,
    schoolConsentDescription,
    consentSnapshot,
    consentDtime
  } = useUpdateConsentToNotifications()

  const [updateNotificationDigestInterval] =
    useUpdateNotificationDigestIntervalMutation()

  const notificationEmailSummary =
    me?.profile?.notification_digest_interval ||
    NotificationDigestInterval.Never

  const initialValues: Values = {
    consentsToCurrent,
    consentsToPrev,
    notificationEmailSummary
  }

  // NOTE: each field submits the form, but we only handle calling mutations per field that changed.
  // We could refactor to rather call the mutaiton on field change, but that's likely less appropriate(?).
  async function handleSubmit(values: Values) {
    // Update user's notification email summary (if changed).
    if (
      values.notificationEmailSummary !== initialValues.notificationEmailSummary
    ) {
      return await updateNotificationDigestInterval({
        variables: {
          interval: values.notificationEmailSummary
        },
        optimisticResponse: {
          updateNotificationDigestInterval: {
            id: me.profile?.id,
            notification_digest_interval: values.notificationEmailSummary
          }
        }
      })
    }

    // If either consent field has changed, update the notification consent.
    // The prev consent will only ever be false (since it will only be included if changed).
    // The current consent will either be true or false.
    if (
      values.consentsToCurrent !== initialValues.consentsToCurrent ||
      values.consentsToPrev !== initialValues.consentsToPrev
    ) {
      return await updateConsentToNotifications(
        values.consentsToCurrent || !!values.consentsToPrev
      )
    }
  }

  const disclosure = useDisclosure()
  const { open } = disclosure

  return (
    <VStack w="100%" gap={open ? "augmentationPanelGutter" : 0} align="stretch">
      <AugmentationPanelRow
        asChild
        bg={open ? "augmentationPanel.fill.muted" : undefined}
      >
        <Button
          onClick={disclosure.onToggle}
          display="flex"
          alignItems="center"
          gap="augmentationPanel"
          w="full"
          cursor="pointer"
          textAlign="left"
        >
          <Pathicon icon="bell-fill" size="16px" />
          <Text as="span" flex="1" textStyle="augmentationPanel" m={0}>
            {t(
              "user_settings.notifications.link",
              "Change notification settings"
            )}
          </Text>
          <Pathicon
            icon={open ? "chevron-down" : "chevron-right"}
            size="16px"
            opacity={0.6}
          />
        </Button>
      </AugmentationPanelRow>
      <Box w="100%">
        <Collapsible.Root open={open}>
          <Collapsible.Content>
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validate={validate}
              enableReinitialize
            >
              {(form) => (
                <Form>
                  <VStack
                    align="stretch"
                    gap="augmentationPanelGutter"
                    w="100%"
                    pl="augmentationPanel"
                    py="augmentationPanelGutter"
                    borderLeftWidth="1px"
                    borderLeftColor="augmentationPanel.border"
                  >
                    <AugmentationPanelRow
                      interactive={false}
                      minH="augmentationPanelRowTouch"
                      py="augmentationPanelGutter"
                    >
                      <ChakraField.Root
                        as={HStack}
                        w="100%"
                        alignItems="center"
                        justifyContent="space-between"
                        gap="augmentationPanel"
                      >
                        <ChakraField.Label
                          htmlFor="notificationEmailSummary"
                          flex="1"
                          {...AUGMENTATION_PANEL_STATIC_TEXT_PROPS}
                        >
                          {t(
                            "user_settings.notifications.label",
                            "Get notifications email summary"
                          )}
                        </ChakraField.Label>
                        <NativeSelect.Root
                          // Ugh, the indicator is positioned absolutely, so we need to set the maxWidth and minWidth to ensure it isn't cut off.
                          maxWidth="85px"
                          minWidth="min-content"
                        >
                          <Field
                            name="notificationEmailSummary"
                            id="notificationEmailSummary"
                            as={NativeSelect.Field}
                            children={[
                              <option
                                key="never"
                                value={NotificationDigestInterval.Never}
                              >
                                {t(
                                  "user_settings.notifications.options.never",
                                  "Never"
                                )}
                              </option>,
                              <option
                                key="daily"
                                value={NotificationDigestInterval.Daily}
                              >
                                {t(
                                  "user_settings.notifications.options.daily",
                                  "Daily"
                                )}
                              </option>,
                              <option
                                key="weekly"
                                value={NotificationDigestInterval.Weekly}
                              >
                                {t(
                                  "user_settings.notifications.options.weekly",
                                  "Weekly"
                                )}
                              </option>
                            ]}
                            onChange={async (
                              e: ChangeEvent<HTMLSelectElement>
                            ) => {
                              await form.setFieldValue(
                                "notificationEmailSummary",
                                e.target.value
                              )
                              await form.submitForm()
                            }}
                            {...AUGMENTATION_PANEL_SELECT_PROPS}
                            minWidth="max-content"
                            h="augmentationPanelRowTouch"
                            minH="augmentationPanelRowTouch"
                          />
                          <NativeSelect.Indicator>
                            <Pathicon icon="chevron-down" />
                          </NativeSelect.Indicator>
                        </NativeSelect.Root>
                      </ChakraField.Root>
                    </AugmentationPanelRow>

                    {!!schoolConsentDescription && (
                      <NotificationConsentSwitchField
                        field="consentsToCurrent"
                        label={schoolConsentDescription}
                      />
                    )}
                    {consentsToPrev && (
                      <NotificationConsentSwitchField
                        field="consentsToPrev"
                        label={consentSnapshot}
                        meta={
                          consentDtime
                            ? moment(consentDtime).fromNow()
                            : undefined
                        }
                      />
                    )}
                  </VStack>
                </Form>
              )}
            </Formik>
          </Collapsible.Content>
        </Collapsible.Root>
      </Box>
    </VStack>
  )
}

export default UserAugmentationNotificationsSettings
